/**
 * `introStore` : ⟦C22 · A-151⟧ le fil qui tient `<Intro>` allumée PAR-DESSUS le changement
 * d'écran que sa propre création déclenche.
 *
 * **Pourquoi ce store existe, et pas un simple `useState` dans `NouvelleRun.tsx`** (le premier
 * jet, cassé en session) : `runStore.creer()` pose `run` dans `runStore`, un store GLOBAL, et
 * `router.tsx` (`Routeur()`) lit CE `run` directement pour décider quoi afficher. À la
 * milliseconde où `creer()` résout, `Routeur` bascule de `<NouvelleRun/>` (branche `!run`) à
 * `<Studio/>` (branche par défaut) et DÉMONTE tout ce qui était sous `NouvelleRun`, y compris
 * un `<Intro>` qui y aurait été niché, avant même sa première image. Constaté en jouant : la
 * chambre apparaissait instantanément, aucune frame de l'animation ne s'affichait.
 *
 * La solution : `<Intro>` est montée par `App.tsx`, AU-DESSUS de `<Routeur/>`, sur l'état de CE
 * store plutôt que sur un état local d'un écran que le routeur peut démonter à tout moment.
 * Elle survit donc, intacte, à la bascule `NouvelleRun` vers `Studio`, et c'est très bien ainsi :
 * la VRAIE chambre est déjà là, montée, tranquille, sous l'overlay opaque de l'intro ; quand
 * l'intro se retire (`terminer()`), il n'y a plus rien à raccorder, elle était déjà prête.
 *
 * Jamais persisté (`localStorage`) : c'est un état de TRANSITION, pas une préférence ni une
 * donnée de run : un F5 pendant l'intro n'a pas de sens à rejouer.
 */

import { create } from "zustand";

import type { GenreAvatar } from "@maxime/shared";

interface IntroStore {
  /** Une intro doit jouer : montée par `App.tsx` tant que c'est vrai. */
  actif: boolean;
  nomChaine: string;
  genre: GenreAvatar | null;
  /** La run existe réellement dans `runStore` (`creer()` a résolu). Tant que c'est faux,
   *  `<Intro>` tient son image finale au lieu de se retirer. */
  pret: boolean;

  /** `NouvelleRun.tsx`, juste avant `creer()` : l'intro doit jouer PENDANT la création. */
  demarrer(nomChaine: string, genre: GenreAvatar): void;
  /** `creer()` a résolu : la run existe, `<Intro>` peut se retirer dès que son minutage le
   *  permet. */
  marquerPret(): void;
  /** `creer()` a échoué (réseau, mode compte) : jamais de run à révéler, on retire l'intro tout
   *  de suite : `NouvelleRun.tsx` reste affiché avec son message d'erreur, il n'a jamais quitté
   *  l'écran (seul `run` fait bouger `Routeur`, et il n'a jamais été posé). */
  annuler(): void;
  /** `<Intro>` a fini de jouer (naturellement ou passée d'un tap) : elle se retire. Le routeur
   *  a DÉJÀ basculé sur la chambre pendant qu'elle jouait, rien à naviguer ici. */
  terminer(): void;
}

export const useIntroStore = create<IntroStore>((set) => ({
  actif: false,
  nomChaine: "",
  genre: null,
  pret: false,

  demarrer(nomChaine, genre) {
    set({ actif: true, nomChaine, genre, pret: false });
  },
  marquerPret() {
    set({ pret: true });
  },
  annuler() {
    set({ actif: false, pret: false });
  },
  terminer() {
    set({ actif: false, pret: false });
  },
}));
